import { api } from './api'

// Types for the custodian data API response
export interface CustodianAccount {
  id: number
  client_identifier: string
  account_identifier: string
  amount: string
  status: string
  account_type: string
  account_name: string
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  deleted_at: string | null
  deleted_by: string | null
}

export interface CustodianClient {
  id: number
  client_identifier: string
  first_name: string
  last_name: string
  date_of_birth: string
  designation: string | null
  company: string | null
  joining_date: string | null
  is_active: boolean
  risk_profile: string | null
  created_at: string
  updated_at: string
}

export interface CustodianContactInfo {
  id: number
  client_id: number
  client_identifier: string
  email_address: string
  emergency_contact_name: string | null
  address_line_1: string | null
  address_line_2: string | null
  street: string | null
  city: string | null
  state: string | null
  country: string | null
  mailing_zip_code: string | null
  social_security_number: string
  phone_number: string | null
  created_at: string
  updated_at: string
}

export interface CustodianHolding {
  id: number
  account_id: number
  security_id: number
  quantity: string
  unit_cost: string
  current_price: string
  market_value: string
  total_cost: string
  unrealized_gain_loss: string
  position_type: string
  acquisition_date: string
  created_at: string
  updated_at: string
}

export interface CustodianPortfolioSummary {
  id: number
  account_id: number
  client_id: number
  client_identifier: string
  total_market_value: string
  total_cost_basis: string
  total_unrealized_gain_loss: string
  holdings_count: number
  equity_percentage: string
  fixed_income_percentage: string
  cash_percentage: string
  last_calculated_at: string
  created_at: string
  updated_at: string
}

export interface CustodianSecurityMaster {
  id: number
  symbol: string
  cusip: string
  security_name: string
  security_type: string
  asset_class: string
  asset_sub_class: string
  sector: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CustodianSecurityPrice {
  id: number
  security_id: number
  price_date: string
  open_price: string | null
  high_price: string
  low_price: string
  close_price: string
  adjusted_close: string | null
  volume: string | null
  created_at: string
  updated_at: string
}

export interface CustodianTables {
  accounts: CustodianAccount[]
  clients: CustodianClient[]
  contact_info: CustodianContactInfo[]
  holdings: CustodianHolding[]
  portfolio_summaries: CustodianPortfolioSummary[]
  securities_master: CustodianSecurityMaster[]
  security_prices: CustodianSecurityPrice[]
}

export interface CustodianTableCounts {
  accounts: number
  clients: number
  contact_info: number
  holdings: number
  portfolio_summaries: number
  securities_master: number
  security_prices: number
}

export interface CustodianData {
  tables: CustodianTables
  table_counts: CustodianTableCounts
  total_tables: number
  total_records: number
}

export interface CustodianDataResponse {
  success: boolean
  message: string
  schema_name: string
  data: CustodianData
  correlation_id: string
  timestamp: string
}

export interface CombinedCustodianData {
  custodianA: CustodianDataResponse | null
  custodianB: CustodianDataResponse | null
}

class CustodianDataService {
  /**
   * Get complete custodian data from custodian_a schema
   */
  async getCustodianAData(): Promise<CustodianDataResponse> {
    try {
      const response = await api.get('/demo-data/schema-complete/custodian_a')
      return response.data
    } catch (error: any) {
      console.error('Error fetching custodian A data:', error)
      throw new Error(error?.response?.data?.message || 'Failed to fetch custodian A data')
    }
  }

  /**
   * Get complete custodian data from custodian_b schema
   */
  async getCustodianBData(): Promise<CustodianDataResponse> {
    try {
      const response = await api.get('/demo-data/schema-complete/custodian_b')
      return response.data
    } catch (error: any) {
      console.error('Error fetching custodian B data:', error)
      throw new Error(error?.response?.data?.message || 'Failed to fetch custodian B data')
    }
  }

  /**
   * Get data from both custodians simultaneously
   */
  async getCombinedCustodianData(): Promise<CombinedCustodianData> {
    try {
      const [custodianAResult, custodianBResult] = await Promise.allSettled([
        this.getCustodianAData(),
        this.getCustodianBData()
      ])

      return {
        custodianA: custodianAResult.status === 'fulfilled' ? custodianAResult.value : null,
        custodianB: custodianBResult.status === 'fulfilled' ? custodianBResult.value : null
      }
    } catch (error: any) {
      console.error('Error fetching combined custodian data:', error)
      throw new Error('Failed to fetch custodian data')
    }
  }
}

export const custodianDataService = new CustodianDataService()